import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Admin Route Protection Component
 * Only allows users with admin privileges to access
 * 
 * Admin determination criteria:
 * 1. user.admin === true
 * 2. user.roles includes 'admin'
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Protected child components
 * @param {string} props.redirectTo - Redirect path when unauthorized (default /home)
 * @param {boolean} props.showAccessDenied - Whether to show access denied page instead of redirecting
 */
const AdminRoute = ({ 
    children, 
    redirectTo = '/home', 
    showAccessDenied = true 
}) => {
    const { user, authenticated, loading } = useAuth();
    const location = useLocation();

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verifying permissions...</p>
                </div>
            </div>
        );
    }

    // Not authenticated
    if (!authenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // Check admin privileges
    const isAdmin = user?.admin === true || user?.roles?.includes('admin');

    if (!isAdmin) {
        if (showAccessDenied) {
            // Show access denied page
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full mx-4">
                        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                            {/* Icon */}
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                                <svg 
                                    className="w-10 h-10 text-red-600" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                </svg>
                            </div>

                            {/* Title */}
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Access Denied
                            </h1>
                            
                            <p className="text-gray-600 mb-6">
                                This page requires admin privileges to access.<br />
                                Please contact the system administrator if you need access.
                            </p>

                            {/* User Information */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-left">
                                <p className="text-gray-500 mb-1">Current User</p>
                                <p className="font-medium text-gray-900">{user?.displayName || 'Unknown User'}</p>
                                <p className="text-gray-500">{user?.email || user?.mail}</p>
                                {user?.roles && user.roles.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-gray-500 mb-1">Assigned Roles</p>
                                        <div className="flex flex-wrap gap-1">
                                            {user.roles.map((role, idx) => (
                                                <span 
                                                    key={idx} 
                                                    className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded"
                                                >
                                                    {role}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Permission Requirements */}
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-sm text-left">
                                <p className="text-orange-800 font-medium mb-1">Permission Requirements</p>
                                <ul className="text-orange-700 space-y-1">
                                    <li>• <code className="bg-orange-100 px-1 rounded">user.admin = true</code></li>
                                    <li>• Or <code className="bg-orange-100 px-1 rounded">user.roles</code> includes <code className="bg-orange-100 px-1 rounded">'admin'</code></li>
                                </ul>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => window.history.back()}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Go Back
                                </button>
                                <a
                                    href="/home"
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Back to Home
                                </a>
                            </div>
                        </div>

                        {/* Footer Note */}
                        <p className="text-center text-gray-400 text-sm mt-4">
                            Attempted Access: <code className="bg-gray-100 px-1 rounded">{location.pathname}</code>
                        </p>
                    </div>
                </div>
            );
        }
        
        // Redirect to specified path
        return <Navigate to={redirectTo} replace />;
    }

    // Has admin permissions, render children
    return children;
};

export default AdminRoute;
